import { useState } from 'react'

const STAGING_API = 'https://api.staging.waithere.com.br'
const PROD_API = 'https://api.waithere.com.br'

const currentUrl =
  typeof window !== 'undefined' ? window.location.href.toLowerCase() : ''

const apiBaseUrl = () => {
  if (currentUrl.includes('localhost') || currentUrl.includes('192.168')) {
    return import.meta.env.VITE_API_URL || 'http://localhost:3007'
  }
  if (currentUrl.includes('staging')) {
    return STAGING_API
  }
  return PROD_API
}

const API_URL = apiBaseUrl()

/**
 * Dark hero + trial form. Pass `sectionId` (e.g. `testar-gratis`).
 */
export default function LeadTrialSection({ sectionId = 'testar-gratis' }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    whatsapp: '',
    instagram: '',
    business_name: '',
  })
  const [status, setStatus] = useState('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')

    try {
      const res = await fetch(`${API_URL}/api/v1/public/onboardings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onboarding: form }),
      })

      const data = await res.json()

      if (res.ok) {
        setStatus('success')
      } else {
        setStatus('error')
        setErrorMsg(
          Array.isArray(data.errors) ? data.errors.join(', ') : 'Erro ao enviar. Tente novamente.'
        )
      }
    } catch {
      setStatus('error')
      setErrorMsg('Erro de conexão. Tente novamente.')
    }
  }

  const inputCls =
    'w-full border border-neutral-200 rounded-lg px-4 py-3 text-[14px] text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white'

  return (
    <section
      id={sectionId}
      className="scroll-mt-24 px-5 py-16 sm:px-8 lg:px-16 lg:py-24"
      style={{ background: '#000000' }}
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 lg:items-center">
          <div className="flex-1 min-w-0">
            <h2 className="text-[34px] sm:text-[46px] lg:text-[56px] font-black tracking-[-1.5px] sm:tracking-[-2px] leading-[1.12] text-white mb-6">
              Seu restaurante<br />
              no próximo nível com{' '}
              <span style={{ color: '#e07b00' }}>tecnologia completa</span>{' '}
              ao seu alcance
            </h2>

            <p className="text-[15px] lg:text-[16px] text-white/70 leading-[1.75] max-w-[440px] mb-8">
              Pedidos de mesa, delivery e retirada, cardápio digital, notificações e muito mais; tudo integrado, pronto para usar.
            </p>

            <a
              href="https://wa.me/5547984916254"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 no-underline px-7 py-[15px] rounded-full text-[15px] font-bold transition-all active:scale-[0.98]"
              style={{ background: '#e07b00', color: '#fff' }}
            >
              Falar com um especialista →
            </a>
          </div>

          <div className="w-full lg:w-[460px] flex-shrink-0 bg-white rounded-2xl p-7 shadow-2xl">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path
                      d="M5 14l6 6L23 8"
                      stroke="#16a34a"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="text-[20px] font-black text-neutral-900">Pronto! 🎉</h3>
                <p className="text-[14px] text-neutral-500 leading-[1.65]">
                  Recebemos seu cadastro. Nossa equipe vai entrar em contato em breve para ativar seu teste grátis.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-[18px] font-black text-neutral-900 mb-1">
                  Comece seu teste grátis de 30 dias agora
                </h3>
                <p className="text-[13px] text-neutral-400 mb-5">
                  Sem cartão de crédito · cancele quando quiser
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <input
                    type="text"
                    placeholder="Seu nome *"
                    required
                    value={form.name}
                    onChange={set('name')}
                    className={inputCls}
                  />

                  <input
                    type="email"
                    placeholder="Seu email pessoal *"
                    required
                    value={form.email}
                    onChange={set('email')}
                    className={inputCls}
                  />

                  <input
                    type="tel"
                    placeholder="Seu WhatsApp *"
                    required
                    value={form.whatsapp}
                    onChange={set('whatsapp')}
                    className={inputCls}
                  />

                  <input
                    type="text"
                    placeholder="Nome do seu negócio *"
                    required
                    value={form.business_name}
                    onChange={set('business_name')}
                    className={inputCls}
                  />

                  <input
                    type="text"
                    placeholder="Instagram do negócio (opcional)"
                    value={form.instagram}
                    onChange={set('instagram')}
                    className={inputCls}
                  />

                  {errorMsg && <p className="text-[13px] text-red-500">{errorMsg}</p>}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-[15px] rounded-lg text-[15px] font-bold text-white transition-all active:scale-[0.98] disabled:opacity-60 mt-1"
                    style={{ background: '#000000' }}
                  >
                    {status === 'loading' ? 'Enviando…' : 'Testar grátis por 30 dias →'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
